import {
  call,
  put,
  fork,
  select,
  take,
  cancelled,
  takeLatest,
  delay,
} from 'redux-saga/effects'
import queryString from 'query-string'
import download from 'downloadjs'
import {
  requestSearch,
  successSearch,
  failureSearch,
  successFetchPermission,
  failFetchPermission,
  successDownload,
  failureDownload,
  CHANGE_KEYWORDS,
  CHANGE_PAGE,
  TOGGLE_OPTION,
  REMOVE_CONDITION,
  SET_RANGE,
  SET_PERIOD,
  TOGGLE_SORT,
  REQUEST_DOWNLOAD,
  SET_OPTIONS,
  Permission,
  faildChangeStatus,
  successChangeStatus,
  changeStatus,
  CHANGE_STATUS,
  SELECT_FILTER_STATUS_CONDITION,
  CHANGE_CONTACT_PERSON,
  changeContactPerson,
  successChangeContactPerson,
  faildChangeContactPerson,
  DELETE_COMPANY_STATUS,
  deleteCompanyStatus,
  successDeleteCompanyStatus,
  faildDeleteCompanyStatus,
  deleteCompanyContactPerson,
  successDeleteCompanyContactPerson,
  faildDeleteCompanyContactPerson,
  DELETE_COMPANY_CONTACT_PERSON,
  SELECT_FILTER_STATUS_UPDATED,
  UPDATE_COMPANY_MEMO,
  updateCompanyMemo,
  successUpdateCompanyMemo,
  updateCompanyFirstInterviewDate,
  successUpdateCompanyFirstInterviewDate,
  UPDATE_COMPANY_FIRST_INTERVIEW_DATE,
  DELETE_COMPANY_FIRST_INTERVIEW_DATE,
  deleteCompanyFirstInterviewDate,
  successDeleteCompanyFirstInterviewDate,
  SELECT_FILTER_FIRST_INTERVIEW_DATE,
  SELECT_FILTER_SOURCING_ROUTE,
  setInitSourcingRoutesCondition,
  SELECT_FILTER_MY_LABEL,
  FETCH_COMPANIES,
} from './actions'
import { callAPIRaw, getBlob } from './common/api'
import { callAPI } from '@/lib/api/common'
import { timestamp, fromEntries } from './utils'
import { normalize, queriesOf, QueryObject } from './libs/queries'
import { validateConditions } from './saga/validate'
import { State } from './reducers'
import {
  ConditionState,
  SortState,
  SourcingRouteId,
} from './reducers/condition'
import { ColumnState } from './reducers/column'
import { setCondition } from './saga/history'
import { ErrorState } from './reducers/error'
import { statusesActions, StatusesResponse } from './actions/statuses'
import {
  contactPersonsActions,
  ContactPersonsResponse,
} from './actions/contactPerson'
import { StatusConditionState } from './reducers/condition'
import { contactPersonsFilterListActions } from './actions/contactPersonsFilterList'
import {
  CONTACT_PERSON_SETTING,
  STATUS_SETTING,
  SUM_OF_CONTACT_PERSON_SETTING,
  SUM_OF_FIRST_INTERVIEW_DATE_SETTING,
  SUM_OF_SOURCING_ROUTE_SETTING,
  SUM_OF_STATUS_SETTING,
  trackEvent,
} from '@/lib/tracking'
import _ from 'lodash'
import {
  CHANGE_SOURCING_ROUTE,
  CREATE_SOURCING_ROUTE,
  REFRESH_SOURCING_ANNOTATION_CANDIDATES,
  SourcingAnnotationsCandidatesResponse,
  UPDATE_SOURCING_ROUTE,
  changeSourcingRoute,
  createSourcingRoute,
  refreshSourcingAnnotationCandidates,
  successFetchCandidates,
  sucessUpdateCompanySouringRoute,
  updateSourcingRoute,
  detouchCompanySourcingRoute,
  successDetouchCompanySourcingRoute,
  DETOUCH_COMPANY_SOURCING_ROUTE,
  successUpdateSourcingRoute,
  DELETE_SOURCING_ROUTE,
  deleteSourcingRoute,
  successDeleteSourcingRoute,
  ADD_COMPANY_MY_LABEL,
  DELETE_COMPANY_MY_LABEL,
  CREATE_MY_LABEL,
  UPDATE_MY_LABEL,
  DELETE_MY_LABEL,
} from './actions/sourcingAnnotation'
import {
  deleteCompanyMyLabelRequest,
  postMyLabel,
  putCompanyMyLabelRequest,
  putMyLabel,
  execDeleteMyLabel,
} from './saga/mylabel'

type Condition = {
  keywords: string
  tags: string
  offset: number
  limit: number
  fields: string
} & SortState &
  QueryObject

type CallCondition = ReturnType<typeof prepareCondition>

type CallStatusCondition = ReturnType<typeof prepareStatusCondition>

const compactObject = (obj: any) => {
  return fromEntries(Object.entries(obj).filter(([_, value]) => !!value))
}

// refs: https://uzabase.slack.com/archives/C9JE6SZNK/p1693281485086959
// AWS WAF の block SQLi ルールに引っかかってしまう問題への対処
export const normalizeTextToAvoidSqlInjectionWafRule = (input: string) => {
  return input.replace(/\(/g, '（').replace(/\)/g, '）')
}

export function buildCondintion(
  conditionState: ConditionState,
  columnState: ColumnState
): Condition {
  let {
    keywords: input,
    tags,
    offset,
    limit,
    sort,
    order,
    selected,
    page,
  } = conditionState
  const { extractedTags, extractedKeywords } = extractTagsAndKeywords(input)
  tags = extractedTags.replace(/^,/, '').replace(/,$/, '')
  const keywords = normalizeTextToAvoidSqlInjectionWafRule(extractedKeywords)
  const fields = buildColumnField(columnState)
  offset = page ? (page - 1) * 50 : offset
  const q = {
    keywords,
    tags,
    offset,
    limit,
    sort,
    order,
    fields,
    page,
    ...queriesOf(selected),
  }
  return <Condition>compactObject(q)
}

function buildColumnField(columnState: ColumnState) {
  const { setting } = columnState
  const fields = setting.join(',')
  return fields
}

export function extractTagsAndKeywords(input: string) {
  const tagRegexp = /(#[^\s]+)/g
  const tagList = input.match(tagRegexp) || []
  const extractedTags = tagList.map((s) => s.replace('#', '')).join(',')
  const extractedKeywords = input
    .split(' ')
    .map((v) => v.replace(tagRegexp, ''))
    .filter((v) => v)
    .join(' ')
  return { extractedTags, extractedKeywords }
}

function* prepareCondition() {
  const conditionState: ConditionState = yield select<
    (state: State) => ConditionState
  >((state) => state.condition)

  const columnState: ColumnState = yield select<(state: State) => ColumnState>(
    (state) => state.column
  )
  return buildCondintion(conditionState, columnState)
}

function* prepareStatusCondition() {
  const conditionState: StatusConditionState = yield select<
    (state: State) => StatusConditionState
  >((state) => state.statusCondition)

  return buildStatusCondition(conditionState)
}

export function buildStatusCondition(conditionState: StatusConditionState) {
  const conditions = conditionState.filterItems.reduce<{
    [key: string]: string
  }>(
    (acc, curr) =>
      curr.conditions.length === 0
        ? acc
        : Object.assign(acc, {
            [curr.itemName]: curr.conditions
              .map((c) => c.id.toString())
              .join(','),
          }),
    {}
  )

  const dateConditions = [
    { key: 'statusUpdated', value: conditionState.statusUpdated },
    { key: 'firstInterviewDate', value: conditionState.firstInterviewDate },
  ]

  dateConditions.forEach(({ key, value }) => {
    if (value) {
      const updatedFromKey = `${key}From`
      const updatedToKey = `${key}To`
      if (value.from) {
        conditions[updatedFromKey] = normalize(value.from)
      }
      if (value.to) {
        conditions[updatedToKey] = normalize(value.to)
      }
    }
  })

  if (conditionState.sourcingRouteIds.length !== 0) {
    conditions.sourcingRouteIds = conditionState.sourcingRouteIds
      .map((e) => e.id)
      .join(',')
  }

  if (conditionState.myLabelIds.length > 0) {
    conditions.myLabelIds = conditionState.myLabelIds.join(',')
  }

  return conditions
}

function* handleRequestDownload() {
  while (true) {
    yield take(REQUEST_DOWNLOAD)
    yield call(downloadCompanies)
  }
}

function* downloadCompanies() {
  yield call(validateConditions)
  const { errors } = yield select<(state: State) => ErrorState>(
    (state) => state.error
  )
  if (errors.length > 0) return

  const condition: CallCondition = yield call(prepareCondition)
  const statusCondition: CallStatusCondition = yield call(
    prepareStatusCondition
  )
  const { payload, error } = yield call(
    getBlob,
    `/api/v2/companies/report?${queryString.stringify(
      condition
    )}&${queryString.stringify(statusCondition)}`
  )
  if (payload && !error) {
    download(payload, `ep-companies-${timestamp()}.xlsx`, payload.type)
    yield put(successDownload())
    yield fork(fetchPermission)
  } else {
    console.error(error)
    yield put(failureDownload(error))
  }
}

const PREFLIGHT_SEARCH = 'PREFLIGHT_SEARCH'
const preflightSearch = () => ({ type: PREFLIGHT_SEARCH })

function* handleChangeCondition() {
  while (true) {
    yield take([
      CHANGE_KEYWORDS,
      CHANGE_PAGE,
      TOGGLE_OPTION,
      SET_OPTIONS,
      REMOVE_CONDITION,
      SET_RANGE,
      SET_PERIOD,
      TOGGLE_SORT,
      SELECT_FILTER_STATUS_CONDITION,
      SELECT_FILTER_STATUS_UPDATED,
      SELECT_FILTER_FIRST_INTERVIEW_DATE,
      SELECT_FILTER_SOURCING_ROUTE,
      SELECT_FILTER_MY_LABEL,
    ])
    yield put(preflightSearch())
  }
}

function* handleRequestSearch() {
  yield takeLatest(PREFLIGHT_SEARCH, fetchCompanies)
}

export function* fetchCompanies() {
  const abortController = new AbortController()
  try {
    yield delay(200)

    yield put(requestSearch())
    yield call(validateConditions)
    const { errors } = yield select<(state: State) => ErrorState>(
      (state) => state.error
    )
    if (errors.length > 0) return

    const condition: CallCondition = yield call(prepareCondition)
    const statusCondition: CallStatusCondition = yield call(
      prepareStatusCondition
    )
    // @ts-ignore
    const response = yield call(
      callAPI,
      `/api/v2/companies?${queryString.stringify(
        condition
      )}&${queryString.stringify(statusCondition)}`,
      {
        ...condition,
        signal: abortController.signal,
      }
    )
    if (response) {
      yield put(successSearch(response))
    } else {
      yield put(failureSearch('failed to fetch response'))
    }
  } finally {
    // @ts-ignore
    if (yield cancelled()) {
      abortController.abort()
    }
  }
}

function* fetchPermission() {
  const res: {
    role: Permission
    latestPlan: boolean
    earlyAccess: boolean
    searchDownloadRestricted: boolean
    searchDownloadAllowed: boolean
  } = yield call(callAPI, '/api/users/me')
  if (res) {
    yield put(
      successFetchPermission(
        res.role,
        res.latestPlan,
        res.searchDownloadRestricted,
        res.searchDownloadAllowed,
        res.earlyAccess
      )
    )
  } else {
    yield put(failFetchPermission('failed to fetch permission'))
  }
}

function* fetchStatuses() {
  yield put(statusesActions.fetchStatuses())
  try {
    const response: StatusesResponse = yield call(callAPI, '/api/statuses')
    yield put(statusesActions.successFetchStatuses(response))
  } catch {
    yield put(statusesActions.failedFetchStatuses('failed to fetch statuses'))
  }
}

function* fetchContactPerson() {
  yield put(contactPersonsActions.fetchContactPersons())
  try {
    const response: ContactPersonsResponse = yield call(callAPI, '/api/users')
    yield put(contactPersonsActions.successFetchContactPersons(response))
  } catch {
    yield put(
      contactPersonsActions.failedFetchContactPersons(
        'failed to fetch statuses'
      )
    )
  }
}

function* fetchContactPersonFilterList() {
  // 無効ユーザーを含む一覧取得
  yield put(contactPersonsFilterListActions.fetchContactPersonsFilterList())
  try {
    const response: ContactPersonsResponse = yield call(
      callAPI,
      '/api/including_invalid_users'
    )
    yield put(
      contactPersonsFilterListActions.successFetchContactPersonsFilterList(
        response
      )
    )
  } catch {
    yield put(
      contactPersonsFilterListActions.failedFetchContactPersonsFilterList(
        'failed to fetch statuses'
      )
    )
  }
}

function* fetchSourcingAnnotationCandidates() {
  try {
    const response: SourcingAnnotationsCandidatesResponse = yield call(
      callAPI,
      '/api/v1/sourcing_annotations/candidates'
    )
    yield put(successFetchCandidates(response))
  } catch {
    console.log('failed to fetch sourcing annotation candidates')
  }
}

function* putStatus(action: ReturnType<typeof changeStatus>) {
  const res: Response = yield call(
    callAPIRaw,
    `/api/companies/${action.payload.companyId}/status`,
    {
      method: 'PUT',
      body: JSON.stringify({
        id: action.payload.id,
      }),
    }
  )

  if (res.ok) {
    yield put(successChangeStatus(action.payload))
    trackEvent(action.payload.user, STATUS_SETTING)
    trackEvent(action.payload.user, SUM_OF_STATUS_SETTING)
  } else {
    yield put(faildChangeStatus('failed to put status'))
  }
}

function* putContactPerson(action: ReturnType<typeof changeContactPerson>) {
  const res: Response = yield call(
    callAPIRaw,
    `/api/companies/${action.payload.companyId}/contact_person`,
    {
      method: 'PUT',
      body: JSON.stringify({
        id: action.payload.id,
      }),
    }
  )

  if (res.ok) {
    yield put(successChangeContactPerson(action.payload))
    trackEvent(action.payload.user, CONTACT_PERSON_SETTING)
    trackEvent(action.payload.user, SUM_OF_CONTACT_PERSON_SETTING)
  } else {
    yield put(faildChangeContactPerson('failed to put contact person'))
  }
}

function* deleteStatus(action: ReturnType<typeof deleteCompanyStatus>) {
  const res: Response = yield call(
    callAPIRaw,
    `/api/companies/${action.payload.companyId}/status`,
    {
      method: 'DELETE',
    }
  )

  if (res.ok) {
    yield put(successDeleteCompanyStatus(action.payload))
    trackEvent(action.payload.user, STATUS_SETTING)
  } else {
    yield put(faildDeleteCompanyStatus('failed to delete company status'))
  }
}

function* deleteContactPerson(
  action: ReturnType<typeof deleteCompanyContactPerson>
) {
  const res: Response = yield call(
    callAPIRaw,
    `/api/companies/${action.payload.companyId}/company_contact_person`,
    {
      method: 'DELETE',
    }
  )

  if (res.ok) {
    yield put(successDeleteCompanyContactPerson(action.payload))
    trackEvent(action.payload.user, CONTACT_PERSON_SETTING)
  } else {
    yield put(
      faildDeleteCompanyContactPerson('failed to delete company status')
    )
  }
}

function* putMemo(action: ReturnType<typeof updateCompanyMemo>) {
  const res: Response = yield call(
    callAPIRaw,
    `/api/pages/companies/${action.payload.companyId}/company_memo`,
    {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify({
        content: action.payload.memo,
      }),
    }
  )

  if (res.ok) {
    yield put(successUpdateCompanyMemo(action.payload))
  } else {
    console.log('Error: updateCompanyMemo failed', res.status)
  }
}

function* putFirstInterviewDate(
  action: ReturnType<typeof updateCompanyFirstInterviewDate>
) {
  const res: Response = yield call(
    callAPIRaw,
    `/api/companies/${action.payload.companyId}/first_interview_date`,
    {
      method: 'PUT',
      body: JSON.stringify({
        firstInterviewDate: action.payload.firstInterviewDate,
      }),
    }
  )

  if (res.ok) {
    yield put(successUpdateCompanyFirstInterviewDate(action.payload))
    trackEvent(action.payload.user, SUM_OF_FIRST_INTERVIEW_DATE_SETTING)
  } else {
    console.log('Error: updateCompanyFirstInterviewDate failed', res.status)
  }
}

function* deleteFirstInterviewDate(
  action: ReturnType<typeof deleteCompanyFirstInterviewDate>
) {
  const res: Response = yield call(
    callAPIRaw,
    `/api/companies/${action.payload.companyId}/first_interview_date`,
    {
      method: 'DELETE',
    }
  )

  if (res.ok) {
    yield put(successDeleteCompanyFirstInterviewDate(action.payload))
    trackEvent(action.payload.user, SUM_OF_FIRST_INTERVIEW_DATE_SETTING)
  } else {
    console.log('Error: deleteCompanyFirstInterviewDate failed', res.status)
  }
}

function* putCompanySourcingRoute(
  action: ReturnType<typeof changeSourcingRoute>
) {
  const res: Response = yield call(
    callAPIRaw,
    `/api/v1/companies/${action.payload.companyId}/sourcing_route`,
    {
      method: 'PUT',
      body: JSON.stringify({
        sourcingRouteId: action.payload.sourcingRouteId,
      }),
    }
  )
  if (res.ok) {
    yield put(sucessUpdateCompanySouringRoute(action.payload))
    trackEvent(action.payload.user, SUM_OF_SOURCING_ROUTE_SETTING)
  } else {
    console.log('Error: udpateCompanySouricngRoute')
  }
}

function* deleteCompanySourcingRoute(
  action: ReturnType<typeof detouchCompanySourcingRoute>
) {
  const res: Response = yield call(
    callAPIRaw,
    `/api/v1/companies/${action.payload.companyId}/sourcing_route`,
    {
      method: 'DELETE',
    }
  )
  if (res.ok) {
    yield put(successDetouchCompanySourcingRoute(action.payload))
    trackEvent(action.payload.user, SUM_OF_SOURCING_ROUTE_SETTING)
  } else {
    console.log('Error: detouchCompanySouricngRoute')
  }
}

function* postSourcingRoute(action: ReturnType<typeof createSourcingRoute>) {
  const res: Response = yield call(callAPIRaw, `/api/v1/sourcing_routes`, {
    method: 'POST',
    body: JSON.stringify({
      sourcingRouteName: action.payload.sourcingRouteName,
    }),
  })
  if (res.ok) {
    yield put(refreshSourcingAnnotationCandidates())
  } else {
    console.log('Error: createSouricngRoute')
  }
}

function* putSourcingRoute(action: ReturnType<typeof updateSourcingRoute>) {
  const res: Response = yield call(
    callAPIRaw,
    `/api/v1/sourcing_routes/${action.payload.id}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        sourcingRouteName: action.payload.name,
      }),
    }
  )
  if (res.ok) {
    yield put(refreshSourcingAnnotationCandidates())
    // 再検索せずに検索結果にソーシングルート名の変更を反映させる
    yield put(successUpdateSourcingRoute(action.payload))
  } else {
    console.log('Error: putSourcingRoute')
  }
}

function* deleteSourcingRouteRequest(
  action: ReturnType<typeof deleteSourcingRoute>
) {
  const res: Response = yield call(
    callAPIRaw,
    `/api/v1/sourcing_routes/${action.payload.id}`,
    {
      method: 'DELETE',
    }
  )
  if (res.ok) {
    yield put(refreshSourcingAnnotationCandidates())
    yield put(successDeleteSourcingRoute(action.payload))
    const ids: SourcingRouteId[] = yield select(
      (state) => state.condition.sourcingRouteIds
    )
    yield put(
      setInitSourcingRoutesCondition(
        ids.filter((e) => e.id != action.payload.id)
      )
    )
    yield fork(fetchCompanies)
  } else {
    console.log('Error: deleteSourcingRoute')
  }
}

export default function* rootSaga() {
  // Initialize state
  yield call(setCondition)
  yield call(fetchPermission)

  yield fork(handleRequestSearch)
  yield fork(handleChangeCondition)
  yield fork(handleRequestDownload)
  yield fork(fetchStatuses)
  yield fork(fetchContactPerson)
  yield fork(fetchContactPersonFilterList)
  yield fork(fetchSourcingAnnotationCandidates)
  // yield fork(handleUrlChange)
  yield takeLatest(FETCH_COMPANIES, fetchCompanies)
  yield takeLatest(CHANGE_STATUS, putStatus)
  yield takeLatest(CHANGE_CONTACT_PERSON, putContactPerson)
  yield takeLatest(DELETE_COMPANY_STATUS, deleteStatus)
  yield takeLatest(DELETE_COMPANY_CONTACT_PERSON, deleteContactPerson)
  yield takeLatest(UPDATE_COMPANY_MEMO, putMemo)
  yield takeLatest(UPDATE_COMPANY_FIRST_INTERVIEW_DATE, putFirstInterviewDate)
  yield takeLatest(
    DELETE_COMPANY_FIRST_INTERVIEW_DATE,
    deleteFirstInterviewDate
  )
  yield takeLatest(CHANGE_SOURCING_ROUTE, putCompanySourcingRoute)
  yield takeLatest(CREATE_SOURCING_ROUTE, postSourcingRoute)
  yield takeLatest(UPDATE_SOURCING_ROUTE, putSourcingRoute)
  yield takeLatest(DELETE_SOURCING_ROUTE, deleteSourcingRouteRequest)
  yield takeLatest(
    REFRESH_SOURCING_ANNOTATION_CANDIDATES,
    fetchSourcingAnnotationCandidates
  )
  yield takeLatest(DETOUCH_COMPANY_SOURCING_ROUTE, deleteCompanySourcingRoute)
  yield takeLatest(ADD_COMPANY_MY_LABEL, putCompanyMyLabelRequest)
  yield takeLatest(DELETE_COMPANY_MY_LABEL, deleteCompanyMyLabelRequest)
  yield takeLatest(CREATE_MY_LABEL, postMyLabel)
  yield takeLatest(UPDATE_MY_LABEL, putMyLabel)
  yield takeLatest(DELETE_MY_LABEL, execDeleteMyLabel)
}
